// src/contexts/WorkspacesContext.js - Refactored for new architecture
import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { getCurrentUser } from '../utils/authService';
import * as workspacesApi from '../../api/workspacesApi';
import { useCategories } from './CategoriesContext';
import { debounce } from 'lodash';
import cacheService from '../../services/cacheService';
import indexedDBService from '../../services/indexedDBService';

const WorkspacesContext = createContext();

export function WorkspacesProvider({ children }) {
  const { categories } = useCategories();

  // ============================================================================
  // STATE
  // ============================================================================
  const [workspaces, setWorkspaces] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedDocumentPublicId, setSelectedDocumentPublicId] = useState(null);
  const [selectedWorkspacePublicId, setSelectedWorkspacePublicId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState('saved');
  const [currentView, setCurrentView] = useState('all');
  // NEW: Sync status state
    const [syncStatus, setSyncStatus] = useState({
      isSyncing: false,
      progress: 0,
      total: 0,
      phase: '',
      isTrusted: false
    });

  // ============================================================================
  // WORKSPACE OPERATIONS
  // ============================================================================




const loadWorkspaces = useCallback(async () => {
  const user = getCurrentUser();
  if (!user) return;
  
  setLoading(true);
  setError(null);
  
  try {
    // Cache-first strategy for trusted devices
    if (cacheService.isTrustedDevice()) {
      console.log('📦 Loading workspaces from cache...');
      
      // Load from cache first (instant)
      const cachedWorkspaces = await indexedDBService.getAllWorkspaces();
      if (cachedWorkspaces && cachedWorkspaces.length > 0) {
        console.log(`✅ Loaded ${cachedWorkspaces.length} workspaces from cache`);
        setWorkspaces(cachedWorkspaces);
        setLoading(false);
        
        // Then sync in background
        console.log('🔄 Starting background sync...');
        cacheService.incrementalSync(user.user_id).then(result => {
          if (result.workspaces > 0) {
            console.log(`🔄 Synced ${result.workspaces} workspaces, refreshing...`);
            indexedDBService.getAllWorkspaces().then(updatedWorkspaces => {
              setWorkspaces(updatedWorkspaces);
            });
          }
        }).catch(error => {
          console.error('Background sync error:', error);
        });
        
        return cachedWorkspaces;
      }
      
      // Cache is EMPTY - trigger FULL SYNC (downloads ALL workspaces + documents)
      console.log('📦 Cache empty - starting FULL sync of all data...');
      await cacheService.fullSync(user.user_id);
      
      // Load workspaces immediately so UI shows something
      const data = await workspacesApi.fetchWorkspaces();
      setWorkspaces(data);
      return data;
    }
    
    // Untrusted device - just load workspaces from API
    console.log('🌐 Loading workspaces from API (untrusted device)...');
    const data = await workspacesApi.fetchWorkspaces();
    setWorkspaces(data);
    return data;
    
  } catch (err) {
    console.error('Error loading workspaces:', err);
    setError(err.message);
    throw err;
  } finally {
    setLoading(false);
  }
}, []);

  const loadAllWorkspaces = loadWorkspaces;

  const loadRecentWorkspaces = useCallback(async (limit = 20) => {
    const user = getCurrentUser();
    if (!user) return;
    
    setLoading(true);
    setError(null);
    setCurrentView('recent');
    
    try {
      const data = await workspacesApi.fetchRecentWorkspaces(limit);
      setWorkspaces(data);
      return data;
    } catch (err) {
      console.error('Error loading recent workspaces:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

// REPLACE the entire createWorkspace function:
const createWorkspace = useCallback(async () => {
  const user = getCurrentUser();
  if (!user) return;

  const realCategories = categories?.filter(cat => !cat.is_virtual);
  const defaultCategory = realCategories?.[0];
  
  if (!defaultCategory) {
    console.error('No real categories available');
    return;
  }

  setLoading(true);
  setError(null);

    try {
      // Create the workspace
      const newWorkspace = await workspacesApi.createWorkspace(defaultCategory.id);
      setWorkspaces(prev => [newWorkspace, ...prev]);
      
      // Select the workspace and clear document
      setSelectedWorkspacePublicId(newWorkspace.public_id);
      setSelectedDocumentPublicId(null);  // ✅ ADD THIS LINE - clear old document
      
      return newWorkspace;
    } catch (err) {
        console.error('Error creating workspace:', err);
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    }, [categories]);



  const updateWorkspaceCategory = useCallback(async (workspacePublicId, categoryId) => {
    const user = getCurrentUser();
    if (!user) return;

    try {
      const workspace = workspaces.find(w => w.public_id === workspacePublicId);
      if (!workspace) return;

      const updatedWorkspace = await workspacesApi.updateWorkspace(workspacePublicId, {
        ...workspace,
        primary_category_id: categoryId
      });

      setWorkspaces(prev => 
        prev.map(w => w.public_id === workspacePublicId ? updatedWorkspace : w)
      );

      return updatedWorkspace;
    } catch (err) {
      console.error('Error updating workspace category:', err);
      setError(err.message);
      throw err;
    }
  }, [workspaces]);



const updateWorkspaceName = useCallback(async (workspacePublicId, name) => {
  const user = getCurrentUser();
  if (!user) return;

  try {
    const workspace = workspaces.find(w => w.public_id === workspacePublicId);
    if (!workspace) return;

    const updatedWorkspace = await workspacesApi.updateWorkspace(workspacePublicId, {
      ...workspace,
      name: name
    });

    setWorkspaces(prev => 
      prev.map(w => w.public_id === workspacePublicId ? updatedWorkspace : w)
    );

    // NEW: Update cache if trusted device
    if (cacheService.isTrustedDevice()) {
      await indexedDBService.saveWorkspace(updatedWorkspace);
      console.log('💾 Workspace name cached:', workspacePublicId);
    }

    return updatedWorkspace;
  } catch (err) {
    console.error('Error updating workspace name:', err);
    setError(err.message);
    throw err;
  }
}, [workspaces]);


const updateWorkspacePreviewPosition = useCallback(async (workspacePublicId, position) => {
  const user = getCurrentUser();
  if (!user) return;

  try {
    const workspace = workspaces.find(w => w.public_id === workspacePublicId);
    if (!workspace) return;

    // ✅ IMMEDIATE STATE UPDATE (optimistic)
    const optimisticWorkspace = {
      ...workspace,
      preview_x: position.preview_x,
      preview_y: position.preview_y,
      preview_width: position.preview_width,
      preview_height: position.preview_height
    };
    
    setWorkspaces(prev => 
      prev.map(w => w.public_id === workspacePublicId ? optimisticWorkspace : w)
    );

    // ✅ Only send the fields that should be updated
    const updatePayload = {
      name: workspace.name,
      primary_category_id: workspace.primary_category_id,
      secondary_categories: workspace.secondary_categories,
      preview_x: position.preview_x,
      preview_y: position.preview_y,
      preview_width: position.preview_width,
      preview_height: position.preview_height
    };

    // ✅ THEN persist to server in background
    const updatedWorkspace = await workspacesApi.updateWorkspace(workspacePublicId, updatePayload);

    // ✅ Merge server response with optimistic update (preserve our changes)
    const finalWorkspace = {
      ...optimisticWorkspace,
      ...updatedWorkspace,
      // But force our preview values to stay
      preview_x: position.preview_x,
      preview_y: position.preview_y,
      preview_width: position.preview_width,
      preview_height: position.preview_height
    };

    setWorkspaces(prev => 
      prev.map(w => w.public_id === workspacePublicId ? finalWorkspace : w)
    );

    return finalWorkspace;
  } catch (err) {
    console.error('Error updating workspace preview position:', err);
    setError(err.message);
    
    // ✅ On error, revert to original workspace
    const originalWorkspace = workspaces.find(w => w.public_id === workspacePublicId);
    if (originalWorkspace) {
      setWorkspaces(prev => 
        prev.map(w => w.public_id === workspacePublicId ? originalWorkspace : w)
      );
    }
    
    throw err;
  }
}, [workspaces]);

  // ============================================================================
  // DOCUMENT OPERATIONS
  // ============================================================================

const loadDocuments = useCallback(async (workspacePublicId) => {
  const user = getCurrentUser();
  if (!user || !workspacePublicId) return;

  setLoading(true);
  setError(null);

  try {
    // Cache-first strategy for trusted devices
    if (cacheService.isTrustedDevice()) {
      console.log('📦 Loading documents from cache for workspace:', workspacePublicId);
      
      // Load from cache first (instant)
      const cachedDocs = await indexedDBService.getDocumentsByWorkspace(workspacePublicId);
      if (cachedDocs && cachedDocs.length > 0) {
        console.log(`✅ Loaded ${cachedDocs.length} documents from cache`);
        
        // Merge cached documents
        setDocuments(prev => {
          const filtered = prev.filter(d => d.workspace_public_id !== workspacePublicId);
          return [...filtered, ...cachedDocs];
        });
        
        setLoading(false);
        
        // Check if cache is stale (>5 minutes since last sync)
        const lastSyncTime = await indexedDBService.getLastSyncTime();
        const cacheAge = lastSyncTime ? Date.now() - new Date(lastSyncTime).getTime() : Infinity;
        const STALE_THRESHOLD = 5 * 60 * 1000; // 5 minutes
        
        if (cacheAge > STALE_THRESHOLD) {
          console.log('🔄 Cache is stale, refreshing in background...');
          workspacesApi.fetchDocuments(workspacePublicId).then(freshData => {
            const freshDocs = freshData.documents || freshData || [];
            
            // Only update if there are differences
            const cachedIds = new Set(cachedDocs.map(d => d.public_id));
            const freshIds = new Set(freshDocs.map(d => d.public_id));
            const hasChanges = cachedIds.size !== freshIds.size || 
                             freshDocs.some(d => !cachedIds.has(d.public_id));
            
            if (hasChanges) {
              console.log('🔄 Documents changed, updating...');
              
              // Update cache
              freshDocs.forEach(doc => {
                indexedDBService.saveDocument(doc).catch(err => {
                  console.error('Error caching document:', err);
                });
              });
              
              // Merge fresh documents
              setDocuments(prev => {
                const filtered = prev.filter(d => d.workspace_public_id !== workspacePublicId);
                return [...filtered, ...freshDocs];
              });
            }
          }).catch(err => {
            console.error('Background document sync error:', err);
          });
        } else {
          console.log(`✅ Cache is fresh (synced ${Math.round(cacheAge / 1000)}s ago), skipping background sync`);
        }
        
        return cachedDocs;
      }
      
      // Cache is empty, fall through to API load
      console.log('📦 Document cache empty, loading from API...');
    }
    
    // Fallback: Load from API (untrusted devices or empty cache)
    console.log('🌐 Loading documents from API...');
    const data = await workspacesApi.fetchDocuments(workspacePublicId);
    const docs = data.documents || data || [];
    
    // Merge instead of replace
    setDocuments(prev => {
      const filtered = prev.filter(d => d.workspace_public_id !== workspacePublicId);
      return [...filtered, ...docs];
    });
    
    // Save to cache if trusted device
    if (cacheService.isTrustedDevice()) {
      console.log('💾 Caching documents...');
      for (const doc of docs) {
        await indexedDBService.saveDocument(doc).catch(err => {
          console.error('Error caching document:', err);
        });
      }
    }
    
    return docs;
    
  } catch (err) {
    console.error('Error loading documents:', err);
    setError(err.message);
    throw err;
  } finally {
    setLoading(false);
  }
}, []);


// NEW: Load ALL documents from ALL workspaces (for search functionality)
const loadAllDocuments = useCallback(async () => {
  const user = getCurrentUser();
  if (!user) return;

  try {
    // Only for trusted devices with cache
    if (cacheService.isTrustedDevice()) {
      console.log('📦 Loading ALL documents from cache for search...');
      
      // Get all documents from cache
      const allCachedDocs = await indexedDBService.getAllDocuments();
      
      if (allCachedDocs && allCachedDocs.length > 0) {
        console.log(`✅ Loaded ${allCachedDocs.length} total documents from cache`);
        setDocuments(allCachedDocs);
        return allCachedDocs;
      }
    }
    
    console.log('⚠️ No cached documents or not trusted device');
    return [];
  } catch (err) {
    console.error('Error loading all documents:', err);
    return [];
  }
}, []);

  const loadDocumentContent = useCallback(async (documentPublicId) => {
  const user = getCurrentUser();
  if (!user || !documentPublicId) return;

  try {
    // Check cache first for trusted devices
    if (cacheService.isTrustedDevice()) {
      const cached = documents.find(d => d.public_id === documentPublicId);
      if (cached && cached.content) {
        return cached; // Already in state with content
      }
    }
    
    // Load from API
    const document = await workspacesApi.fetchDocument(documentPublicId);
    
    setDocuments(prev => {
      const existing = prev.find(d => d.public_id === documentPublicId);
      if (existing) {
        return prev.map(d => d.public_id === documentPublicId ? document : d);
      } else {
        return [...prev, document];
      }
    });
    
    return document;
  } catch (err) {
    console.error('Error loading document content:', err);
    return null;
  }
}, [documents]);

  const createDocument = useCallback(async (workspacePublicId) => {
    const user = getCurrentUser();
    if (!user || !workspacePublicId) return;

    setLoading(true);
    setError(null);

    try {
      const newDocument = await workspacesApi.createDocument(workspacePublicId);
      setDocuments(prev => [...prev, newDocument]);
      return newDocument;
    } catch (err) {
      console.error('Error creating document:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteDocument = useCallback(async (documentPublicId) => {
    const user = getCurrentUser();
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      await workspacesApi.deleteDocument(documentPublicId);
      setDocuments(prev => prev.filter(d => d.public_id !== documentPublicId));
      
      if (selectedDocumentPublicId === documentPublicId) {
        setSelectedDocumentPublicId(null);
      }
      
      return true;
    } catch (err) {
      console.error('Error deleting document:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectedDocumentPublicId]);

  const updateDocument = useCallback(async (documentPublicId, updates) => {
    const user = getCurrentUser();
    if (!user) return;

    setSaveStatus('saving');

    try {
      const updatedDocument = await workspacesApi.updateDocument(documentPublicId, updates);
      
      setDocuments(prev =>
        prev.map(d => d.public_id === documentPublicId ? updatedDocument : d)
      );
      
      setSaveStatus('saved');
      return updatedDocument;
    } catch (err) {
      console.error('Error updating document:', err);
      setSaveStatus('error');
      setError(err.message);
      throw err;
    }
  }, []);

  const debouncedUpdateDocument = useCallback(
    debounce(updateDocument, 1000),
    [updateDocument]
  );

  const updateDocumentContent = useCallback((documentPublicId, content) => {
    setDocuments(prev =>
      prev.map(d => d.public_id === documentPublicId ? { ...d, content } : d)
    );
    
    return debouncedUpdateDocument(documentPublicId, { content });
  }, [debouncedUpdateDocument]);

  const updateDocumentTitle = useCallback((documentPublicId, title) => {
  setDocuments(prev =>
    prev.map(d => d.public_id === documentPublicId ? { ...d, title } : d)
  );
  
  // NEW: Update cache optimistically if trusted device
  if (cacheService.isTrustedDevice()) {
    // Get the document from current state to update cache
    setDocuments(prev => {
      const doc = prev.find(d => d.public_id === documentPublicId);
      if (doc) {
        const updatedDoc = { ...doc, title, modified_date: new Date().toISOString() };
        indexedDBService.saveDocument(updatedDoc).catch(err => {
          console.error('Error caching document title:', err);
        });
      }
      return prev; // Don't modify state here, already updated above
    });
    console.log('💾 Document title cached:', documentPublicId);
  }
  
  return debouncedUpdateDocument(documentPublicId, { title });
}, [debouncedUpdateDocument]);

  const updateDocumentTags = useCallback((documentPublicId, tags) => {
    setDocuments(prev =>
      prev.map(d => d.public_id === documentPublicId ? { ...d, tags } : d)
    );
    
    return debouncedUpdateDocument(documentPublicId, { tags });
  }, [debouncedUpdateDocument]);


  // ============================================================================
  // SELECTION OPERATIONS
  // ============================================================================

  const selectDocument = useCallback((documentPublicId) => {
    setSelectedDocumentPublicId(documentPublicId);
  }, []);

  // ✅ ADD THIS ENTIRE FUNCTION:
const selectWorkspace = useCallback(async (workspacePublicId) => {
  setSelectedWorkspacePublicId(workspacePublicId);
  
  // Load documents for this workspace
  const docs = await loadDocuments(workspacePublicId);
  
  // If documents exist, select and load the first one
  if (docs && docs.length > 0) {
    setSelectedDocumentPublicId(docs[0].public_id);
    await loadDocumentContent(docs[0].public_id);
  } else {
    // No documents - clear document selection
    setSelectedDocumentPublicId(null);
  }
}, [loadDocuments, loadDocumentContent]);


const deleteWorkspace = useCallback(async (workspacePublicId) => {
  const user = getCurrentUser();
  if (!user) return;

  setLoading(true);
  setError(null);

  try {
    await workspacesApi.deleteWorkspace(workspacePublicId);
    
    // Remove workspace from state
    const remainingWorkspaces = workspaces.filter(w => w.public_id !== workspacePublicId);
    setWorkspaces(remainingWorkspaces);
    
    // If we deleted the selected workspace, select the first remaining one
    if (selectedWorkspacePublicId === workspacePublicId) {
      if (remainingWorkspaces.length > 0) {
        const nextWorkspace = remainingWorkspaces[0];
        
        // Select workspace
        setSelectedWorkspacePublicId(nextWorkspace.public_id);
        
        // Load documents and select first one
        const docs = await loadDocuments(nextWorkspace.public_id);
        if (docs && docs.length > 0) {
          setSelectedDocumentPublicId(docs[0].public_id);
          await loadDocumentContent(docs[0].public_id);
        } else {
          setSelectedDocumentPublicId(null);
        }
      } else {
        // No workspaces left - clear selection
        setSelectedWorkspacePublicId(null);
        setSelectedDocumentPublicId(null);
      }
    }
    
    return true;
  } catch (err) {
    console.error('Error deleting workspace:', err);
    setError(err.message);
    throw err;
  } finally {
    setLoading(false);
  }
}, [workspaces, selectedWorkspacePublicId, loadDocuments, loadDocumentContent]);

// ✅ ADD THIS ENTIRE FUNCTION:
const getSelectedWorkspace = useCallback(() => {
    if (!selectedWorkspacePublicId) return null;
    return workspaces.find(w => w.public_id === selectedWorkspacePublicId);
  }, [selectedWorkspacePublicId, workspaces]);


  const getSelectedDocument = useCallback(() => {
    if (!selectedDocumentPublicId) return null;
    return documents.find(d => d.public_id === selectedDocumentPublicId);
  }, [selectedDocumentPublicId, documents]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

useEffect(() => {
  const initializeWorkspace = async () => {
    await loadWorkspaces();
    
    // NEW: Load ALL documents from cache for search functionality
    if (cacheService.isTrustedDevice()) {
      await loadAllDocuments();
    }
    
    // After workspaces load, check for last accessed
    const user = getCurrentUser();
    if (user?.last_accessed_workspace_id) {
      await selectWorkspace(user.last_accessed_workspace_id);
    }
  };
  
  initializeWorkspace();
}, []);


  useEffect(() => {
    if (selectedDocumentPublicId) {
      loadDocumentContent(selectedDocumentPublicId);
    }
  }, [selectedDocumentPublicId]);

  useEffect(() => {
    return () => {
      debouncedUpdateDocument.cancel();
    };
  }, [debouncedUpdateDocument]);


  
  // NEW: Subscribe to cache sync progress
      useEffect(() => {
        const isTrusted = cacheService.isTrustedDevice();
        setSyncStatus(prev => ({ ...prev, isTrusted }));

        // Listen for sync progress updates
        const unsubscribe = cacheService.onProgress((progress) => {
          setSyncStatus({
            isSyncing: progress.current < progress.total,
            progress: progress.current,
            total: progress.total,
            phase: progress.phase,
            isTrusted
          });
        });

        return () => unsubscribe();
      }, []);

  /// ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  const value = {
    // State
    workspaces,
    documents,
    selectedDocumentPublicId,
    selectedWorkspacePublicId,
    loading,
    error,
    saveStatus,
    currentView,
    syncStatus,  // NEW: Add sync status for trusted device caching
    
    // Workspace operations
    createWorkspace,
    deleteWorkspace,
    loadWorkspaces,
    loadAllWorkspaces,
    loadRecentWorkspaces,
    updateWorkspaceCategory,
    updateWorkspaceName,
    updateWorkspacePreviewPosition,
    
    // Document operations
    createDocument,
    deleteDocument,
    updateDocument: debouncedUpdateDocument,
    updateDocumentContent,
    updateDocumentTitle,
    updateDocumentTags,
    loadDocuments,
    loadAllDocuments, 
    loadDocumentContent,
    
    // Selection
    selectDocument,
    selectWorkspace,  
    getSelectedDocument,  
    getSelectedWorkspace, 
    
    // Refresh
    refreshWorkspaces: loadWorkspaces
  };

  return (
    <WorkspacesContext.Provider value={value}>
      {children}
    </WorkspacesContext.Provider>
  );
}

export function useWorkspaces() {
  const context = useContext(WorkspacesContext);
  if (!context) {
    throw new Error('useWorkspaces must be used within a WorkspacesProvider');
  }
  return context;
}